import React from 'react';
import { Participant } from '../../types/registration';

interface WarningModalProps {
  isOpen: boolean;
  dancer: Participant | null;
  onClose: () => void;
  isDarkMode?: boolean;
}

/**
 * Warning modal specifically for routine dancers that cannot be removed directly
 */
const WarningModal: React.FC<WarningModalProps> = ({
  isOpen,
  dancer,
  onClose,
  isDarkMode = false
}) => {
  if (!isOpen || !dancer) return null;

  // Determine routines information
  const routines = (() => {
    // Handle custom routineNames property if it exists
    const customRoutineNames = (dancer as any).routineNames;
    if (Array.isArray(customRoutineNames) && customRoutineNames.length > 0) {
      return customRoutineNames;
    }
    
    // Handle custom fromRoutines property if it exists
    const customFromRoutines = (dancer as any).fromRoutines;
    if (Array.isArray(customFromRoutines) && customFromRoutines.length > 0) {
      return customFromRoutines;
    }
    
    // Standard properties from the Participant type
    if (dancer.routineName || dancer.fromRoutine) {
      return [dancer.routineName || dancer.fromRoutine];
    }
    
    return [] as string[];
  })();

  const hasMultipleRoutines = routines.length > 1;
  const primaryRoutine = routines.length > 0 ? routines[0] : "routine";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className={`relative w-full max-w-md p-6 rounded-lg shadow-lg ${
        isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
      }`}>
        <h3 className="text-lg font-semibold mb-4">Cannot Remove Dancer</h3>
        
        <p className="mb-3">
          <span className="font-bold">{dancer.firstName} {dancer.lastName}</span> is part of{" "}
          {hasMultipleRoutines ? "multiple routines" : "a routine"} and cannot be removed directly.
        </p>
        
        <div className={`p-4 mb-4 text-sm rounded ${
          isDarkMode 
            ? 'bg-blue-900/30 text-blue-300' 
            : 'bg-blue-100 text-blue-700'
        }`}>
          {hasMultipleRoutines ? (
            <>
              To remove this dancer, you must first remove them from all {routines.length} routines:
              <ul className="list-disc ml-5 mt-2">
                {routines.map((routine, index) => (
                  <li key={index}>{routine}</li>
                ))}
              </ul>
            </>
          ) : (
            <>
              To remove this dancer, you must first remove them from the routine "{primaryRoutine}" 
              or remove the routine from your registration.
            </>
          )}
        </div>
        
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-md ${
              isDarkMode 
                ? 'bg-blue-600 hover:bg-blue-500 text-white' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            Understood
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarningModal; 